import { useCallback } from 'react';
import { Dialog } from '@/components/ui';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import { tabAnalyzerPromptsApi } from '@/services/tab-analyzer-prompts-api';
import type { ITabAnalyzerPromptVersion, TabId } from '@skystone/shared';

const SOURCE_LABELS: Record<string, string> = {
  baseline: 'Baseline (pre-versioning)',
  admin_update: 'Edited',
  admin_restore: 'Restored',
};

function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

interface PromptVersionHistoryProps {
  tabId: TabId;
  /** Called after a successful restore so the parent can re-seed the editor. */
  onRestored: () => void;
}

export function PromptVersionHistory({ tabId, onRestored }: Readonly<PromptVersionHistoryProps>) {
  const load = useCallback(() => tabAnalyzerPromptsApi.getVersions(tabId), [tabId]);
  const onRestore = useCallback(
    async (version: number) => {
      await tabAnalyzerPromptsApi.restore(tabId, version);
      onRestored();
    },
    [tabId, onRestored],
  );

  return (
    <VersionHistoryPanel<ITabAnalyzerPromptVersion>
      load={load}
      onRestore={onRestore}
      sourceLabel={sourceLabel}
      listTestId={`prompt-versions-${tabId}`}
      renderPreview={(v, onClose) => (
        <Dialog open onClose={onClose} title={`Prompt — v${v.version}`}>
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-background p-3 font-mono text-xs text-foreground">
            {v.systemPrompt}
          </pre>
        </Dialog>
      )}
    />
  );
}

export default PromptVersionHistory;
